/** SF-4651: transcript files chosen in the wizard. */
import { useSyncExternalStore } from 'react';
import type { TranscriptResult } from './api/demoBuildsClient';
import { transcriptRefusal } from './validation';

export interface ChosenTranscript {
  uid: string;
  file: File;
  /** Why the wizard will not send it (checked before upload). */
  refusal: string | null;
}

let uidSeq = 0;

/**
 * Checks every file again, in order, against the ones kept before it. Run on the whole list after
 * any add or remove, so a file refused only for the five-file limit is taken once there is room.
 * `held` is how many transcripts the build already holds (SF-4729): they use up places first.
 */
export function recheckTranscripts(list: ChosenTranscript[], held = 0): ChosenTranscript[] {
  let kept = held;
  return list.map((t) => {
    const refusal = transcriptRefusal(t.file, kept);
    if (!refusal) kept += 1;
    return { ...t, refusal };
  });
}

/** Adds files after the ones already chosen. */
export function addTranscripts(
  current: ChosenTranscript[],
  files: File[],
  held = 0,
): ChosenTranscript[] {
  const added = files.map((file) => {
    uidSeq += 1;
    return { uid: `t${uidSeq}`, file, refusal: null };
  });
  return recheckTranscripts([...current, ...added], held);
}

/** Removes one file; the rest are checked again. */
export function removeTranscript(
  current: ChosenTranscript[],
  uid: string,
  held = 0,
): ChosenTranscript[] {
  return recheckTranscripts(
    current.filter((t) => t.uid !== uid),
    held,
  );
}

/**
 * Files the wizard chose for a build it has just created, waiting for the build's own page to
 * send them (SF-4729). Kept in memory only: after a reload the page asks for the files again.
 */
const handedOver = new Map<string, ChosenTranscript[]>();

export function handOverTranscripts(buildId: string, files: ChosenTranscript[]) {
  handedOver.set(buildId, files);
}

/** The files handed over for this build, if any (still held until dropped). */
export const handedOverTranscripts = (buildId: string): ChosenTranscript[] =>
  handedOver.get(buildId) ?? [];

export function dropHandedOverTranscripts(buildId: string) {
  handedOver.delete(buildId);
}

/**
 * How sf-api's answer for each uploaded file is shown. sf-api sends its reason as plain words
 * (services/demoTranscriptService.js REFUSALS); `fallback` is shown when it sends none.
 */
export const TRANSCRIPT_OUTCOMES: Record<
  TranscriptResult['status'],
  { label: string; color: string; fallback: string }
> = {
  read: { label: 'Read', color: 'success', fallback: 'Eva will use this transcript.' },
  refused: { label: 'Refused', color: 'error', fallback: 'This file could not be read.' },
};

/** The outcome of one uploaded file, in plain words (an unknown status counts as refused). */
export function transcriptOutcome(result: TranscriptResult) {
  const outcome = TRANSCRIPT_OUTCOMES[result.status] ?? TRANSCRIPT_OUTCOMES.refused;
  return { ...outcome, detail: result.reason || outcome.fallback };
}

/**
 * SF-4729: what sf-api answered for each file uploaded to a build, kept for the build (not for
 * the panel that sent them), so the answers stay on the build's page once Eva starts drafting
 * and the waiting panel is gone. In memory only, like the hand-over: a reload forgets them.
 */
const uploadOutcomes = new Map<string, TranscriptResult[]>();
const outcomeListeners = new Set<() => void>();
const NO_OUTCOMES: TranscriptResult[] = [];

export function addUploadOutcomes(buildId: string, results: TranscriptResult[]) {
  if (results.length === 0) return;
  uploadOutcomes.set(buildId, [...(uploadOutcomes.get(buildId) ?? []), ...results]);
  outcomeListeners.forEach((l) => l());
}

const subscribeOutcomes = (listener: () => void) => {
  outcomeListeners.add(listener);
  return () => {
    outcomeListeners.delete(listener);
  };
};

/** Every upload answer for this build so far, oldest first. Re-renders when one is added. */
export const useUploadOutcomes = (buildId: string | undefined): TranscriptResult[] =>
  useSyncExternalStore(subscribeOutcomes, () =>
    buildId ? (uploadOutcomes.get(buildId) ?? NO_OUTCOMES) : NO_OUTCOMES,
  );

/** Tests only: forget every upload answer between test cases. */
export function resetUploadOutcomes() {
  uploadOutcomes.clear();
  outcomeListeners.forEach((l) => l());
}
